/**
 * Histórico de operaciones: todo lo que el portal hizo en un rango.
 *
 * Reemplaza a la pantalla de auditoría, que mezclaba ingresos con operaciones
 * y obligaba a leer JSON crudo. Acá cada fila es una cosa que pasó: una
 * licencia registrada, una salida marcada, una jornada resuelta.
 *
 * Los rechazados y los que fallaron también son operaciones —y suelen ser los
 * que uno viene a buscar—, así que se muestran con el mismo peso.
 */

import { useCallback, useEffect, useState } from 'react';
import { Search, Loader2 } from 'lucide-react';

import { mensajeDe, type OperacionHistorica } from '../../api';
import { Aviso } from '../../components/Aviso';
import { useSesion } from '../../components/useSesion';
import { CampoFecha } from '../../components/CampoFecha';
import { EnvoltorioTabla, FilaVacia } from '../../components/Tabla';
import { aDdMmAaaa, fechaHoraLegible, fechaHoy } from '../../lib/fechas';
import { historico } from './api';

const CLASES = [
  ['', 'Todas'],
  ['IMPACTO', 'Impactos'],
  ['RESOLUCION', 'Resoluciones'],
] as const;

const RESULTADOS = [
  ['', 'Todos'],
  ['OK', 'Registrados'],
  ['RECHAZADO', 'Rechazados'],
  ['ERROR', 'Con error'],
] as const;

/** Etiquetas de negocio: la tabla no muestra los identificadores internos. */
const ETIQUETA: Readonly<Record<string, string>> = {
  SALIDA_ARTIFICIAL: 'Salida al fin de turno',
  LICENCIA_ASISTENCIA_VIAJE: 'Licencia asistencia al viaje',
  LICENCIA_SIN_TAREA: 'Licencia sin tarea',
  CONFIRMA_ASISTENCIA: 'Confirma la asistencia',
  DESCARTA: 'Descarta la jornada',
  DERIVA: 'Deriva el caso',
};

/** El resultado se dice con una palabra, no con el identificador del back. */
const ETIQUETA_RESULTADO: Readonly<Record<string, string>> = {
  OK: 'Registrado',
  RECHAZADO: 'Rechazado',
  ERROR: 'Error',
};

export function Historico(): React.JSX.Element {
  // Las marcas de tiempo se muestran en la zona operativa del portal.
  const { modulo } = useSesion();
  const [fechaDesde, setFechaDesde] = useState(() => fechaHoy(-7));
  const [fechaHasta, setFechaHasta] = useState(() => fechaHoy());
  const [dni, setDni] = useState('');
  const [clase, setClase] = useState('');
  const [resultado, setResultado] = useState('');

  const [operaciones, setOperaciones] = useState<readonly OperacionHistorica[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);

  const buscar = useCallback(async () => {
    setBuscando(true);
    try {
      const { operaciones: datos } = await historico({
        fechaDesde,
        fechaHasta,
        ...(dni.trim() ? { dni: dni.trim() } : {}),
        ...(clase ? { clase } : {}),
        ...(resultado ? { resultado } : {}),
      });
      setOperaciones(datos);
      setError(null);
    } catch (err) {
      setOperaciones([]);
      setError(mensajeDe(err));
    } finally {
      setBuscando(false);
    }
  }, [fechaDesde, fechaHasta, dni, clase, resultado]);

  // Primera carga. Después la búsqueda es explícita, como en la grilla.
  useEffect(() => {
    void buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="historico">
      <form
        className="banda banda--filtros"
        onSubmit={(ev) => {
          ev.preventDefault();
          void buscar();
        }}
      >
        <CampoFecha etiqueta="Desde" required value={fechaDesde} onChange={setFechaDesde} />
        <CampoFecha etiqueta="Hasta" required value={fechaHasta} onChange={setFechaHasta} />
        <label className="campo">
          Dni
          <input
            type="text"
            inputMode="numeric"
            placeholder="todos"
            value={dni}
            onChange={(ev) => setDni(ev.target.value)}
          />
        </label>
        <label className="campo">
          Tipo
          <select value={clase} onChange={(ev) => setClase(ev.target.value)}>
            {CLASES.map(([v, t]) => (
              <option key={v} value={v}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          Resultado
          <select
            value={resultado}
            onChange={(ev) => {
              setResultado(ev.target.value);
              // Una resolución no tiene resultado: filtrar por uno la excluye,
              // y dejar "Resoluciones" elegido daría siempre vacío.
              if (ev.target.value !== '' && clase === 'RESOLUCION') setClase('');
            }}
          >
            {RESULTADOS.map(([v, t]) => (
              <option key={v} value={v}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <div className="acciones">
          <button type="submit" disabled={buscando}>
            {buscando ? (
              <Loader2 className="girando" size={16} aria-hidden="true" />
            ) : (
              <Search size={16} aria-hidden="true" />
            )}
            {buscando ? 'Buscando…' : 'Buscar'}
          </button>
        </div>
      </form>

      {error !== null && <Aviso tono="error">{error}</Aviso>}

      <EnvoltorioTabla
        pie={
          <>
            <span>
              <strong>{operaciones.length}</strong>
              {' operación(es)'}
            </span>
            <span>Se muestran las 300 más recientes del rango.</span>
          </>
        }
      >
        <table className="tabla">
          <thead>
            <tr>
              <th scope="col" className="num">Fecha y hora</th>
              <th scope="col">Operación</th>
              <th scope="col">Resultado</th>
              <th scope="col" className="num">Dni</th>
              <th scope="col" className="num">Jornada</th>
              <th scope="col">Usuario</th>
              <th scope="col">Detalle</th>
            </tr>
          </thead>
          <tbody>
            {operaciones.length === 0 ? (
              <FilaVacia columnas={7}>
                No hay operaciones en ese rango. Probá otras fechas o quitá los filtros.
              </FilaVacia>
            ) : (
              operaciones.map((o, i) => (
                <tr key={`${o.fechaHora}|${o.dni}|${o.operacion}|${String(i)}`}>
                  <td className="num">{fechaHoraLegible(o.fechaHora, modulo?.zona)}</td>
                  <td>{ETIQUETA[o.operacion] ?? o.operacion}</td>
                  <td>
                    {o.resultado === null ? (
                      <span className="nota">—</span>
                    ) : (
                      <span className="pastilla" data-resultado={o.resultado}>
                        {ETIQUETA_RESULTADO[o.resultado] ?? o.resultado}
                      </span>
                    )}
                  </td>
                  <td className="num">{o.dni}</td>
                  <td className="num">{aDdMmAaaa(o.fechaOperativa)}</td>
                  <td>{o.usuario}</td>
                  <td className="truncado" title={o.detalle ?? o.referencia ?? undefined}>
                    {o.detalle ?? o.referencia ?? '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </EnvoltorioTabla>
    </section>
  );
}
